import { List, ListItem, ListItemIcon, ListItemText, Typography, Box, LinearProgress, Button, CircularProgress, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import useSetupProgress, { CHECKLIST_ITEMS } from 'hooks/useSetupProgress';
import { hasPermission } from 'utils/permissions';

const ACCENT = 'primary.main';

export default function SetupChecklist({ onNavigate }) {
  const { progress, loading } = useSetupProgress();
  const navigate = useNavigate();

  if (loading || !progress) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 120 }}>
        <CircularProgress size={24} sx={{ color: ACCENT }} />
      </Box>
    );
  }

  const handleItemClick = (item, done) => {
    if (done || !hasPermission(item.permission)) return;
    const target = item.action ? `${item.path}?action=${item.action}` : item.path;
    navigate(target);
    if (onNavigate) onNavigate();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Setup Progress
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {progress.completedCount}/{progress.totalCount} complete
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress.percent}
        sx={{ height: 8, borderRadius: 4, mb: 1.5, bgcolor: 'grey.200', '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }}
      />
      <List dense disablePadding>
        {CHECKLIST_ITEMS.map((item) => {
          const done = !!progress.steps?.[item.key];
          const canAccess = hasPermission(item.permission);
          // Show the row to everyone; clicking only works with permission.
          // Owners get the full list regardless.
          const allowed = !done && canAccess;
          return (
            <Tooltip
              key={item.key}
              title={!done && !canAccess ? 'You don’t have permission — ask your workspace owner' : ''}
              placement="left"
            >
            <ListItem
              disablePadding
              sx={{
                py: 0.5,
                cursor: allowed ? 'pointer' : 'default',
                borderRadius: 1,
                '&:hover': allowed ? { bgcolor: 'grey.100' } : {},
              }}
              onClick={allowed ? () => handleItemClick(item, done) : undefined}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {done ? (
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: { textDecoration: done ? 'line-through' : 'none', color: done ? 'text.secondary' : 'text.primary' },
                }}
              />
              {!done && (
                <Button
                  size="small"
                  disabled={!canAccess}
                  onClick={(e) => {
                    if (!canAccess) return;
                    e.stopPropagation();
                    handleItemClick(item, done);
                  }}
                  sx={{ textTransform: 'none', ...(canAccess ? { color: ACCENT } : {}) }}
                >
                  {canAccess ? 'Go' : 'No access'}
                </Button>
              )}
            </ListItem>
            </Tooltip>
          );
        })}
      </List>
    </Box>
  );
}
